// src/videoLib.mjs
// ─────────────────────────────────────────────────────────────────────────
// Pure helpers for the Listing Video LIBRARY feature (video stored server-side
// in Netlify Blobs, uploaded in chunks, selectable into mockup sets like any
// template). NO DOM, NO network, NO React — safe to import from App.jsx
// (webpack) AND from node --test. Keep it pure so the chunk math, validation,
// budget and membership rules are unit-testable without a browser.
//
// Contract note: the transport is chunked because a listing video is now a
// real 10–80MB file (Etsy's hard cap is 100MB). The old ~4MB ceiling was the
// FUNCTION REQUEST body limit, not Blobs — so we slice into ~3.5MB parts and
// each part is a separate small request.
// ─────────────────────────────────────────────────────────────────────────

// ── Limits (single source of truth; the edge function mirrors the budget) ──
export const PART_SIZE = Math.floor(3.5 * 1024 * 1024);       // ~3.5MB upload chunk (safely under the fn body limit)
export const MAX_VIDEO_BYTES = 80 * 1024 * 1024;              // 80MB practical cap (Etsy hard cap is 100MB)
export const MAX_TOTAL_VIDEO_BYTES = 250 * 1024 * 1024;       // per-user budget: 250MB total is the ONLY limit (no video-count cap)
export const MANUAL_MAX_BYTES = Math.floor(4.5 * 1024 * 1024);// PROVEN suite manual-send ceiling: ~4.5MB binary → ~6MB base64 = Netlify fn body limit
export const MIN_DURATION_SEC = 5;                            // Etsy listing-video rules …
export const MAX_DURATION_SEC = 15;                           // … 5–15 seconds
export const ALLOWED_VIDEO_EXT = ["mp4", "mov", "avi", "webm", "mpeg", "mpg"];
// <input accept> string reused by every video picker in the app.
export const VIDEO_ACCEPT = "video/mp4,video/quicktime,video/x-msvideo,video/webm,video/mpeg,.mp4,.mov,.avi,.webm,.mpeg,.mpg";

export function fmtMB(bytes) {
  return (Number(bytes || 0) / 1048576).toFixed(1);
}

// Lowercased extension without the dot ("clip.MP4" → "mp4"), or "".
export function videoExt(name) {
  const m = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : "";
}

// True when a picked File (by name OR mime) is a video we route to the library
// rather than the image/PSD template intake.
export function isVideoFile(name, mimeType) {
  if (mimeType && /^video\//i.test(mimeType)) return true;
  return ALLOWED_VIDEO_EXT.includes(videoExt(name));
}

// ── Chunk math ────────────────────────────────────────────────────────────
// Byte ranges that tile [0, sizeBytes) into parts of at most partSize. The
// index IS the reassembly order — concatenating parts 0..n-1 reproduces the
// file exactly. Sum of part sizes === sizeBytes.
export function partByteRanges(sizeBytes, partSize = PART_SIZE) {
  const total = Math.max(0, Math.floor(Number(sizeBytes) || 0));
  const ps = Math.max(1, Math.floor(partSize));
  const ranges = [];
  let start = 0, index = 0;
  while (start < total) {
    const end = Math.min(total, start + ps);
    ranges.push({ index, start, end, size: end - start });
    start = end;
    index += 1;
  }
  return ranges;
}

export function computeParts(sizeBytes, partSize = PART_SIZE) {
  return partByteRanges(sizeBytes, partSize).length;
}

// ── Validation (format + Etsy duration + practical size) ────────────────────
// metaFailed = true when the browser couldn't read the clip's duration (codec/
// container quirk). We don't dead-end on that — accept with a warn, since Etsy
// may still take it and the seller confirms the length.
export function validateVideoFile({ name, sizeBytes, durationSec, metaFailed } = {}) {
  const ext = videoExt(name);
  if (!ALLOWED_VIDEO_EXT.includes(ext)) {
    return { ok: false, error: `Unsupported format ".${ext || "?"}". Etsy accepts MP4, MOV, AVI, WEBM, or MPEG.` };
  }
  const size = Number(sizeBytes) || 0;
  if (size <= 0) return { ok: false, error: "That file looks empty — pick a different video." };
  if (size > MAX_VIDEO_BYTES) {
    return { ok: false, error: `Video is ${fmtMB(size)} MB. The limit is ${MAX_VIDEO_BYTES / 1048576} MB — trim or export it smaller and try again.` };
  }
  if (!metaFailed && durationSec != null) {
    if (durationSec < MIN_DURATION_SEC || durationSec > MAX_DURATION_SEC) {
      return { ok: false, error: `Video is ${durationSec.toFixed(1)}s long. Etsy requires ${MIN_DURATION_SEC}–${MAX_DURATION_SEC} seconds — trim it with CapCut, iMovie, or any editor and try again.` };
    }
  }
  if (metaFailed) {
    return { ok: true, warn: "Uploaded, but we couldn't verify the length in your browser. If it isn't 5–15 seconds, Etsy will reject it at publish — replace it if unsure." };
  }
  return { ok: true };
}

// ── Per-user storage budget (total bytes ONLY — no video-count cap) ──────────
// existing = the user's current library video manifests ([{sizeBytes,…}]).
export function checkVideoBudget(existing, newSizeBytes) {
  const list = Array.isArray(existing) ? existing : [];
  const used = list.reduce((s, v) => s + (Number(v && v.sizeBytes) || 0), 0);
  if (used + (Number(newSizeBytes) || 0) > MAX_TOTAL_VIDEO_BYTES) {
    return { ok: false, error: `That would exceed your ${MAX_TOTAL_VIDEO_BYTES / 1048576} MB video storage (${fmtMB(used)} MB used). Delete one to make room.` };
  }
  return { ok: true };
}

// ── Set membership → MOCKUP_PROFILES broadcast shape ────────────────────────
// A set carries at most ONE video via profile.videoId (single field = the
// one-video-per-set rule is structural). The broadcast ref is DERIVED from
// membership and is metadata-only — NEVER base64 (the suite localStorage-
// caches MOCKUP_PROFILES).
export function deriveProfileVideo(profile, libVideos) {
  const id = profile && profile.videoId;
  if (!id) return null; // no video attached → truly none (deleting a video clears profile.videoId)
  // hasVideo is derived from the DURABLE profile.videoId, NOT from whether the video-library
  // manifest has loaded into `libVideos` yet. Gating hasVideo on the manifest created a
  // load-race window (profiles ready, library still loading async) where a set WITH a video
  // broadcast video:null — which both hid the video in the suite UI AND, worse, made the
  // publish leg SILENTLY skip it (resolveSetVideoRef returns early on !hasVideo, no warn →
  // members lose the video with no signal). Metadata (name/size/duration) fills in from the
  // manifest WHEN available; a not-yet-loaded manifest yields null metadata but NEVER
  // downgrades hasVideo. (A genuinely dangling videoId — deleted without clearing the
  // profile, which cleanupDeletedVideos normally prevents — now degrades to the studio's
  // on-demand resolve → a LOUD skip-warn at publish, not a silent "no video".)
  const lv = (Array.isArray(libVideos) ? libVideos : []).find(v => v && v.videoId === id);
  return {
    hasVideo: true,
    file_name: lv ? lv.file_name : null,
    sizeBytes: lv ? lv.sizeBytes : null,
    durationSec: lv && lv.durationSec != null ? lv.durationSec : null,
  };
}

// Attach a video to a set (replaces any existing one — one per set). videoId
// null/"" clears it. Returns a NEW profile object.
export function setProfileVideo(profile, videoId) {
  const next = { ...(profile || {}) };
  if (videoId) next.videoId = videoId;
  else delete next.videoId;
  return next;
}

// Can the manual step-3 "Generate & Send" path carry this clip's bytes today?
// The suite's Etsy video leg goes through a body-limited function (~4.5MB
// binary proven). Larger clips publish via SETS once the attach arc ships.
export function fitsManualSend(sizeBytes) {
  return (Number(sizeBytes) || 0) <= MANUAL_MAX_BYTES;
}
